/**
 * Shared number / percentage formatters for every stats surface. Every
 * commander tab and deck pane re-implemented these independently with
 * slightly different digit defaults — this module is the single source
 * of truth. Defaults match the most common caller usage.
 */

/**
 * `1.234` → `"1.23"`. Returns an em-dash placeholder for null/NaN so
 * stats tiles don't render literal `NaN`.
 */
export const formatNumber = (value, digits = 2) => {
    if (value == null || Number.isNaN(value)) return '—';
    const parsed = typeof value === 'number' ? value : parseFloat(value);
    if (Number.isNaN(parsed)) return '—';
    return parsed.toFixed(digits);
};

/**
 * `12345.7` → `"12,346"`. Integer rounding with locale-aware thousands
 * separators and null-safe handling.
 */
export const formatInt = (value) => {
    if (value == null || Number.isNaN(value)) return '—';
    const parsed = typeof value === 'number' ? value : parseFloat(value);
    if (Number.isNaN(parsed)) return '—';
    return Math.round(parsed).toLocaleString();
};

/**
 * Truncate (floor) to one decimal instead of rounding, so values like
 * `5.97` render as `"5.9"` rather than `"6.0"`. Use this for any scale
 * that has a hard cap (bracket tops out at 5, saltiness grade buckets,
 * etc.) — a rounded display can otherwise show a value outside the scale.
 *
 * `emptyPlaceholder` is returned for null/NaN input so callers can choose
 * whether an un-loaded value reads as "0.0" or "—" alongside neighbours.
 */
export const truncateToOneDecimal = (value, emptyPlaceholder = '0.0') => {
    if (value == null) return emptyPlaceholder;
    const parsed = typeof value === 'number' ? value : parseFloat(value);
    if (Number.isNaN(parsed)) return emptyPlaceholder;
    return (Math.floor(parsed * 10) / 10).toFixed(1);
};

/**
 * `0.347` → `"35%"`. Null/undefined returns `0%` to avoid visual gaps in
 * chip counts; pass `nullFallback` to override.
 */
export const formatPct = (rate, { nullFallback = '0%' } = {}) => {
    if (rate == null) return nullFallback;
    return `${Math.round(rate * 100)}%`;
};

/**
 * `0.3473` → `"34.7%"`. Use when you want one decimal of precision on a
 * rate (e.g. saltiness scores).
 */
export const formatPctFixed = (rate, digits = 1) => {
    if (rate == null || Number.isNaN(rate)) return '0%';
    const parsed = typeof rate === 'number' ? rate : parseFloat(rate);
    if (Number.isNaN(parsed)) return '0%';
    return `${(parsed * 100).toFixed(digits)}%`;
};
